import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Users,
  Mail,
  Settings,
  Menu,
  X,
  Home,
  LogOut,
  Image,
  UserCog,
  HelpCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import adminApi from '@/lib/adminApi';

// Admin Pages
import LoginPage from './LoginPage';
import AdminHome from './AdminHome';
import ContentEditor from './ContentEditor';
import MembersManager from './MembersManager';
import MessagesManager from './MessagesManager';
import SettingsManager from './SettingsManager';
import UsersManager from './UsersManager';
import ImagesManager from './ImagesManager';
import AdminGuide from './AdminGuide';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_afc4fae4-3e91-49c0-b442-1e22d639f2c6/artifacts/5tm3hua0_Logo%20PF%402x.png";

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('admin_token');

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Verify token is still valid
        const response = await adminApi.get(`/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
      } finally {
        setLoading(false);
      }
    };

    // First, seed data if needed (idempotent, public)
    adminApi.post(`/seed`).catch(() => {});
    checkAuth();
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setUser(null);
    toast.success('Déconnexion réussie');
  };

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Tableau de bord', exact: true },
    { path: '/admin/content', icon: FileText, label: 'Contenus' },
    { path: '/admin/images', icon: Image, label: 'Images' },
    { path: '/admin/members', icon: Users, label: 'Membres' },
    { path: '/admin/messages', icon: Mail, label: 'Messages' },
    { path: '/admin/settings', icon: Settings, label: 'Paramètres' },
    { path: '/admin/help', icon: HelpCircle, label: 'Guide' },
    ...(user?.is_superadmin ? [{ path: '/admin/users', icon: UserCog, label: 'Administrateurs' }] : [])
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  // Show loading
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#1e3a8a] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show login if not authenticated
  if (!user) {
    return (
      <>
        <LoginPage onLogin={handleLogin} />
        <Toaster position="bottom-right" />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-[#1e3a8a] text-white transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={LOGO_URL} 
                alt="Place Financière BFC" 
                className="h-10 w-auto bg-white rounded p-1"
              />
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white/60 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg font-inter text-sm transition-colors ${
                isActive(item.path, item.exact)
                  ? 'bg-white/10 text-white'
                  : 'text-white/70 hover:bg-white/5 hover:text-white'
              }`}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon size={20} />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 space-y-2">
          {/* User info */}
          <div className="px-4 py-2">
            <p className="text-sm font-inter text-white/90">{user.name}</p>
            <p className="text-xs text-white/50">{user.email}</p>
          </div>
          
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-3 text-white/70 hover:text-white font-inter text-sm transition-colors"
            data-testid="admin-back-to-site"
          >
            <Home size={20} />
            Voir le site
          </Link>
          
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 text-white/70 hover:text-red-400 font-inter text-sm transition-colors w-full"
            data-testid="admin-logout-btn"
          >
            <LogOut size={20} />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 lg:px-8 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-slate-600 hover:text-[#1e3a8a]"
              data-testid="admin-menu-toggle"
            >
              <Menu size={24} />
            </button>
            <h1 className="font-playfair text-xl font-semibold text-[#0F172A]">
              {navItems.find(item => isActive(item.path, item.exact))?.label || 'Administration'}
            </h1>
            <div className="flex items-center gap-4">
              <span className="hidden sm:block text-sm text-slate-500 font-inter">
                {user.name}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          <Routes>
            <Route path="/" element={<AdminHome />} />
            <Route path="/content" element={<ContentEditor />} />
            <Route path="/images" element={<ImagesManager />} />
            <Route path="/members" element={<MembersManager />} />
            <Route path="/messages" element={<MessagesManager />} />
            <Route path="/settings" element={<SettingsManager />} />
            <Route path="/help" element={<AdminGuide />} />
            {user.is_superadmin && (
              <Route path="/users" element={<UsersManager currentUser={user} />} />
            )}
          </Routes>
        </main>
      </div>

      <Toaster position="bottom-right" />
    </div>
  );
};

export default AdminDashboard;
